import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardActions,
  CardContent,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Link,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Language as LanguageIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  NotificationsActive as ReportIcon,
  CheckCircle as EnabledIcon,
  Cancel as DisabledIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationService, type OrganizationSiteResponse } from '../services/organizationService';
import {
  createRequestedSchedule,
  disableSchedule,
  enableSchedule,
  getSiteReportRules,
  getSiteSchedules,
  onboardMandatorySchedules,
  type NotificationReportRule,
  type NotificationSchedule,
} from '../services/notificationGovernanceService';
import { getSiteLimitForPlan, getSiteLimitMessage } from '../utils/siteLimits';
import { analyzeUrl, extractErrorMessages } from '../api/brandingApi';
import type { BrandingAnalyzeResponse } from '../api/brandingApi';

type SitemapValidationState = {
  status: 'idle' | 'validating' | 'success' | 'error';
  validatedUrl?: string;
  result?: BrandingAnalyzeResponse;
  message?: string;
};

const initialSitemapValidation: SitemapValidationState = { status: 'idle' };
const SITEMAP_URL_FORMAT_MESSAGE = 'Use a lowercase sitemap URL ending with .xml, for example https://example.com/sitemap.xml.';

const isSitemapValidationCurrent = (state: SitemapValidationState, sitemapUrl: string) => (
  state.status === 'success' && state.validatedUrl === sitemapUrl.trim()
);

const validateSitemapUrlFormat = (sitemapUrl: string): string | null => {
  if (sitemapUrl !== sitemapUrl.toLowerCase() || !sitemapUrl.endsWith('.xml')) {
    return SITEMAP_URL_FORMAT_MESSAGE;
  }
  return null;
};

const getSitemapCheckLabel = (status: CheckResult['status']): string => {
  if (status === 'PASS') return 'Found';
  if (status === 'FAIL') return 'Needs review';
  if (status === 'WARNING') return 'Review';
  return 'Info';
};

async function validateSitemapSource(
  siteUrl: string,
  sitemapUrl: string,
  setValidation: React.Dispatch<React.SetStateAction<SitemapValidationState>>
) {
  const trimmedSiteUrl = siteUrl.trim();
  const trimmedSitemapUrl = sitemapUrl.trim();

  if (!trimmedSiteUrl || !trimmedSitemapUrl) {
    setValidation({ status: 'error', message: 'Enter the site URL and sitemap URL before validating.' });
    return;
  }

  const sitemapFormatError = validateSitemapUrlFormat(trimmedSitemapUrl);
  if (sitemapFormatError) {
    setValidation({ status: 'error', message: sitemapFormatError });
    return;
  }

  try {
    new URL(trimmedSiteUrl);
    new URL(trimmedSitemapUrl);
  } catch {
    setValidation({ status: 'error', message: 'Enter valid http:// or https:// URLs before validating.' });
    return;
  }

  setValidation({ status: 'validating', validatedUrl: trimmedSitemapUrl });

  try {
    const result = await analyzeUrl({ url: trimmedSiteUrl, scanType: 'SITEMAP', sitemapUrl: trimmedSitemapUrl });
    const hasFailure = result.checks.some((check) => check.status === 'FAIL');
    setValidation({
      status: hasFailure ? 'error' : 'success',
      validatedUrl: trimmedSitemapUrl,
      result,
      message: hasFailure
        ? 'The sitemap responded, but validation found issues that should be reviewed before saving.'
        : 'Sitemap verified. The file exists and returned parseable sitemap content.',
    });
  } catch (err) {
    setValidation({
      status: 'error',
      validatedUrl: trimmedSitemapUrl,
      message: extractErrorMessages(err) || 'Unable to validate this sitemap URL.',
    });
  }
}

interface SitemapValidationPanelProps {
  siteUrl: string;
  sitemapUrl: string;
  validation: SitemapValidationState;
  disabled?: boolean;
  onValidate: () => void;
}

const SitemapValidationPanel: React.FC<SitemapValidationPanelProps> = ({ siteUrl, sitemapUrl, validation, disabled, onValidate }) => {
  const trimmedSitemapUrl = sitemapUrl.trim();
  const isCurrent = isSitemapValidationCurrent(validation, trimmedSitemapUrl);
  const checks = validation.result?.checks ?? [];
  const canValidate = Boolean(siteUrl.trim() && trimmedSitemapUrl) && !disabled && validation.status !== 'validating';

  return (
    <Paper variant="outlined" sx={{ mt: 1.5, p: 2, borderColor: isCurrent ? 'success.light' : 'divider', bgcolor: isCurrent ? 'success.50' : 'background.default' }}>
      <Box display="flex" justifyContent="space-between" gap={1.5} alignItems="flex-start" flexWrap="wrap">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>Sitemap source check</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Validate that the XML file exists and can be parsed before this site becomes a source of truth.
          </Typography>
        </Box>
        <Button
          variant={isCurrent ? 'outlined' : 'contained'}
          size="small"
          onClick={onValidate}
          disabled={!canValidate}
          startIcon={validation.status === 'validating' ? <CircularProgress size={14} /> : isCurrent ? <CheckCircleIcon /> : undefined}
        >
          {validation.status === 'validating' ? 'Validating...' : isCurrent ? 'Validated' : 'Validate sitemap'}
        </Button>
      </Box>

      {trimmedSitemapUrl && (
        <Link href={trimmedSitemapUrl} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ display: 'inline-block', mt: 1, wordBreak: 'break-all' }}>
          Open sitemap XML for review
        </Link>
      )}

      {validation.status === 'success' && validation.result && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 1.5 }}>
          <Typography variant="body2" fontWeight={700}>{validation.message}</Typography>
          <Typography variant="caption" display="block">
            {validation.result.domain} · Score {validation.result.overallScore}/100 · {checks.length} checks
          </Typography>
        </Alert>
      )}

      {validation.status === 'error' && (
        <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mt: 1.5 }}>
          {validation.message || 'Sitemap validation failed.'}
        </Alert>
      )}

      {checks.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          {checks.slice(0, 5).map((check) => (
            <Box key={`${check.category}-${check.checkName}`} display="flex" gap={1} alignItems="flex-start">
              <Chip
                label={getSitemapCheckLabel(check.status)}
                size="small"
                color={check.status === 'PASS' ? 'success' : check.status === 'FAIL' ? 'error' : 'warning'}
                variant="outlined"
                sx={{ minWidth: 96 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700}>{check.checkName.replace(/_/g, ' ')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">{check.message}</Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {!isCurrent && validation.status === 'idle' && trimmedSitemapUrl && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
          This sitemap has not been validated yet. Saving is enabled after a successful check.
        </Typography>
      )}
    </Paper>
  );
};

const normalizePlanTierLabel = (planTier?: string): string => {
  const normalized = (planTier || '').trim().toUpperCase();
  return normalized === 'FREE' ? 'BASIC' : normalized || 'N/A';
};

const getDefaultSiteTierForPlan = (planTier?: string): string => {
  const normalized = (planTier || '').trim().toUpperCase();
  if (normalized === 'PRO' || normalized === 'PROFESSIONAL') return 'PRO';
  if (normalized === 'AGENCY') return 'PRO';
  if (normalized === 'BASIC' || normalized === 'FREE') return 'BASIC';
  return 'STARTER';
};

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

interface SiteNotificationReportsSectionProps {
  site: OrganizationSiteResponse;
  expanded: boolean;
  onToggle: () => void;
  operationsEnabled: boolean;
}

function SiteNotificationReportsSection({ site, expanded, onToggle, operationsEnabled }: SiteNotificationReportsSectionProps) {
  const queryClient = useQueryClient();
  const siteTier = (site.siteTier || 'BASIC').toUpperCase();

  const rulesQuery = useQuery({
    queryKey: ['site-notification-rules', site.id, siteTier],
    queryFn: () => getSiteReportRules(siteTier),
    enabled: expanded && !!site.id,
    staleTime: 30_000,
  });

  const schedulesQuery = useQuery({
    queryKey: ['site-notification-schedules', site.id],
    queryFn: () => getSiteSchedules(site.id),
    enabled: expanded && !!site.id,
    staleTime: 15_000,
    refetchInterval: expanded ? 30_000 : false,
  });

  const schedulesByReport = useMemo(() => {
    const map = new Map<string, NotificationSchedule>();
    schedulesQuery.data?.forEach((schedule) => {
      map.set(schedule.reportType, schedule);
    });
    return map;
  }, [schedulesQuery.data]);

  const effectiveCustomerId = useMemo(() => {
    const fromSite = site.customerId?.trim();
    if (fromSite) return fromSite;

    return schedulesQuery.data
      ?.map((schedule) => schedule.customerId?.trim())
      .find((customerId) => Boolean(customerId));
  }, [schedulesQuery.data, site.customerId]);

  const mandatoryRules = rulesQuery.data?.filter((rule) => rule.isMandatory) ?? [];
  const specialRules = rulesQuery.data?.filter((rule) => rule.isSpecialRequest) ?? [];
  const hasCustomerContext = Boolean(effectiveCustomerId);

  const refreshNotificationReports = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['site-notification-rules', site.id, siteTier] }),
      queryClient.invalidateQueries({ queryKey: ['site-notification-schedules', site.id] }),
    ]);
  };

  const onboardMandatoryMutation = useMutation({
    mutationFn: () =>
      onboardMandatorySchedules(site.id, {
        customerId: String(effectiveCustomerId),
        siteTier,
      }),
    onSuccess: refreshNotificationReports,
  });

  const requestScheduleMutation = useMutation({
    mutationFn: (rule: NotificationReportRule) =>
      createRequestedSchedule({
        tenantId: String(site.tenantId),
        customerId: String(effectiveCustomerId),
        siteId: String(site.id),
        reportType: rule.reportType,
        frequency: rule.defaultFrequency,
        cronExpression: rule.defaultCronExpression,
        priority: rule.defaultPriority,
        slaMinutes: rule.defaultSlaMinutes,
        inputDataTemplate: '',
      }),
    onSuccess: refreshNotificationReports,
  });

  const toggleScheduleMutation = useMutation({
    mutationFn: (schedule: NotificationSchedule) =>
      schedule.isEnabled ? disableSchedule(schedule.id) : enableSchedule(schedule.id),
    onSuccess: refreshNotificationReports,
  });

  const renderContent = () => {
    if (rulesQuery.isLoading || schedulesQuery.isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (rulesQuery.error || schedulesQuery.error) {
      return <Alert severity="error">Failed to load notification reports for this site.</Alert>;
    }

    return (
      <>
        {!hasCustomerContext && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Customer context is missing, so report onboarding actions are disabled for this site.
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Eligible reports
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Report</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Default Cadence</TableCell>
                <TableCell>Current Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rulesQuery.data?.map((rule) => {
                const current = schedulesByReport.get(rule.reportType);
                const isMissing = !current;
                return (
                  <TableRow key={rule.reportType} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {rule.reportType.replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          size="small"
                          label={rule.isMandatory ? 'Mandatory' : 'Requested'}
                          color={rule.isMandatory ? 'warning' : 'secondary'}
                          variant="outlined"
                        />
                        <Chip size="small" label={rule.siteTier} variant="outlined" />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rule.defaultFrequency}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rule.defaultCronExpression}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {current ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={current.isEnabled ? 'Scheduled' : 'Disabled'}
                            color={current.isEnabled ? 'success' : 'default'}
                            icon={current.isEnabled ? <EnabledIcon /> : <DisabledIcon />}
                          />
                          <Chip
                            size="small"
                            label={current.isMandatory ? 'Mandatory' : current.onboardingMode || 'Special'}
                            variant="outlined"
                          />
                        </Stack>
                      ) : (
                        <Chip size="small" label="Not scheduled" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {rule.isSpecialRequest && isMissing && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          disabled={!operationsEnabled || !hasCustomerContext || requestScheduleMutation.isPending}
                          onClick={() => requestScheduleMutation.mutate(rule)}
                        >
                          Request
                        </Button>
                      )}
                      {rule.isMandatory && isMissing && (
                        <Typography variant="caption" color="text.secondary">
                          Use bulk onboarding
                        </Typography>
                      )}
                      {current && !isMissing && (
                        <Button
                          size="small"
                          onClick={() => toggleScheduleMutation.mutate(current)}
                          disabled={!operationsEnabled || toggleScheduleMutation.isPending}
                        >
                          {current.isEnabled ? 'Disable' : 'Enable'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!rulesQuery.data || rulesQuery.data.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No report rules are configured for this site tier.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Current schedules
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Report</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedulesQuery.data?.map((schedule) => (
                <TableRow key={schedule.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {schedule.reportType.replace(/_/g, ' ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {schedule.onboardingMode || 'SPECIAL'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={schedule.isEnabled ? 'Enabled' : 'Disabled'}
                        color={schedule.isEnabled ? 'success' : 'default'}
                        icon={schedule.isEnabled ? <EnabledIcon /> : <DisabledIcon />}
                      />
                      <Chip
                        size="small"
                        label={schedule.isMandatory ? 'Mandatory' : 'Requested'}
                        color={schedule.isMandatory ? 'warning' : 'secondary'}
                        variant="outlined"
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>{schedule.frequency}</TableCell>
                  <TableCell>{formatDateTime(schedule.nextRunAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => toggleScheduleMutation.mutate(schedule)}
                      disabled={!operationsEnabled || toggleScheduleMutation.isPending}
                    >
                      {schedule.isEnabled ? 'Disable' : 'Enable'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {(!schedulesQuery.data || schedulesQuery.data.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No schedules created yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Card variant="outlined">
      <CardActions sx={{ justifyContent: 'space-between', alignItems: 'flex-start', px: 2, py: 1.5 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <ReportIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">Notification Reports</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Manage scheduled and requested reports for this site tier.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
          <Chip label={`Tier: ${siteTier}`} size="small" color="primary" variant="outlined" />
          <Chip label={`${mandatoryRules.length} mandatory`} size="small" color="warning" variant="outlined" />
          <Chip label={`${specialRules.length} requestable`} size="small" color="secondary" variant="outlined" />
          {mandatoryRules.length > 0 && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!operationsEnabled || !hasCustomerContext || onboardMandatoryMutation.isPending}
              onClick={() => onboardMandatoryMutation.mutate()}
            >
              Onboard mandatory reports
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={onToggle}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </Button>
        </Stack>
      </CardActions>

      <Collapse in={expanded}>
        <Divider />
        <CardContent>{renderContent()}</CardContent>
      </Collapse>
    </Card>
  );
}

const OrganizationDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const queryClient = useQueryClient();
  const [orgUsersExpanded, setOrgUsersExpanded] = useState(false);
  const [agencyUsersExpanded, setAgencyUsersExpanded] = useState(false);
  const [expandedSiteIds, setExpandedSiteIds] = useState<Set<number>>(new Set());
  const [propertiesExpandedSiteIds, setPropertiesExpandedSiteIds] = useState<Set<number>>(new Set());
  const [notificationsExpandedSiteIds, setNotificationsExpandedSiteIds] = useState<Set<number>>(new Set());
  const [editingOrganization, setEditingOrganization] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [deletingOrganization, setDeletingOrganization] = useState(false);
  const [creatingSite, setCreatingSite] = useState(false);
  const [editingSite, setEditingSite] = useState<OrganizationSiteResponse | null>(null);
  const [createSitemapValidation, setCreateSitemapValidation] = useState<SitemapValidationState>(initialSitemapValidation);
  const [editSitemapValidation, setEditSitemapValidation] = useState<SitemapValidationState>(initialSitemapValidation);
  const [siteForm, setSiteForm] = useState({
    siteName: '',
    siteUrl: '',
    sitemapUrl: '',
    timezone: '',
    customerName: '',
    customerEmail: '',
    isActive: true,
  });
  const [deletingSite, setDeletingSite] = useState<OrganizationSiteResponse | null>(null);

  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['organization-detail', orgId],
    queryFn: () => organizationService.getOrganization(orgId as string),
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });

  const totalSites = organization?.totalSites ?? 0;
  const operationsEnabled = (organization?.managedBy || '').toUpperCase() === 'SELF';
  const siteLimit = getSiteLimitForPlan(organization?.planTier);
  const canCreateSite = totalSites < siteLimit;
  const addSiteDisabled = !canCreateSite;
  const addSiteDisabledReason = canCreateSite
    ? ''
    : getSiteLimitMessage(organization?.planTier);
  const sortedSites = useMemo(() => {
    if (!organization?.sites) return [];
    return organization.sites;
  }, [organization?.sites]);
  const editingInitialSitemapUrl = editingSite?.sitemapUrl?.trim() || '';
  const currentSiteFormSitemapUrl = siteForm.sitemapUrl.trim();
  const editingSitemapChanged = Boolean(editingSite) && currentSiteFormSitemapUrl !== editingInitialSitemapUrl;
  const canCreateSiteWithSitemap = isSitemapValidationCurrent(createSitemapValidation, currentSiteFormSitemapUrl);
  const canEditSiteWithSitemap = !editingSitemapChanged || isSitemapValidationCurrent(editSitemapValidation, currentSiteFormSitemapUrl);

  const organizationUsers = organization?.organizationUsers || [];
  const agencyUsers = organization?.agencyUsers || [];

  const refreshOrganization = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['organization-detail', orgId] }),
      queryClient.invalidateQueries({ queryKey: ['organizations'] }),
    ]);
  };

  const updateOrganizationMutation = useMutation({
    mutationFn: () => organizationService.updateOrganization(orgId as string, { name: organizationName.trim() }),
    onSuccess: async () => {
      setEditingOrganization(false);
      await refreshOrganization();
    },
  });

  const createSiteMutation = useMutation({
    mutationFn: () => organizationService.createSite(orgId as string, {
      siteName: siteForm.siteName.trim(),
      siteUrl: siteForm.siteUrl.trim(),
      sitemapUrl: siteForm.sitemapUrl.trim(),
      timezone: siteForm.timezone.trim() || 'UTC',
      customerName: siteForm.customerName.trim() || undefined,
      customerEmail: siteForm.customerEmail.trim() || undefined,
      isActive: siteForm.isActive,
      mode: 'EARLY_STAGE',
      siteTier: getDefaultSiteTierForPlan(organization?.planTier),
    }),
    onSuccess: async () => {
      setCreatingSite(false);
      await refreshOrganization();
    },
  });

  const deleteOrganizationMutation = useMutation({
    mutationFn: () => organizationService.deleteOrganization(orgId as string),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      navigate('/organization');
    },
  });

  const updateSiteMutation = useMutation({
    mutationFn: () => {
      if (!editingSite) {
        throw new Error('No site selected');
      }
      return organizationService.updateSite(editingSite.id, {
        name: siteForm.siteName.trim(),
        siteUrl: siteForm.siteUrl.trim(),
        sitemapUrl: siteForm.sitemapUrl.trim(),
        timezone: siteForm.timezone.trim() || undefined,
        customerName: siteForm.customerName.trim() || undefined,
        customerEmail: siteForm.customerEmail.trim() || undefined,
        isActive: siteForm.isActive,
      });
    },
    onSuccess: async () => {
      setEditingSite(null);
      await refreshOrganization();
    },
  });

  const deleteSiteMutation = useMutation({
    mutationFn: () => {
      if (!deletingSite) {
        throw new Error('No site selected');
      }
      return organizationService.deleteSite(deletingSite.id);
    },
    onSuccess: async () => {
      setDeletingSite(null);
      await refreshOrganization();
    },
  });

  useEffect(() => {
    if (sortedSites.length === 0) return;
    setExpandedSiteIds((prev) => {
      if (prev.size > 0) return prev;
      return new Set([sortedSites[0].id]);
    });
    setPropertiesExpandedSiteIds((prev) => {
      if (prev.size > 0) return prev;
      return new Set([sortedSites[0].id]);
    });
    setNotificationsExpandedSiteIds((prev) => {
      if (prev.size > 0) return prev;
      return new Set([sortedSites[0].id]);
    });
  }, [sortedSites]);

  const toggleSiteExpanded = (siteId: number) => {
    setExpandedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const togglePropertiesExpanded = (siteId: number) => {
    setPropertiesExpandedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const toggleNotificationsExpanded = (siteId: number) => {
    setNotificationsExpandedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const openEditOrganization = () => {
    if (!organization) return;
    setOrganizationName(organization.name || '');
    setEditingOrganization(true);
  };

  const resetSiteForm = () => {
    setSiteForm({
      siteName: '',
      siteUrl: '',
      sitemapUrl: '',
      timezone: 'UTC',
      customerName: '',
      customerEmail: '',
      isActive: true,
    });
  };

  const openCreateSite = () => {
    resetSiteForm();
    setCreateSitemapValidation(initialSitemapValidation);
    setCreatingSite(true);
  };

  const closeCreateSite = () => {
    setCreatingSite(false);
    createSiteMutation.reset();
    setCreateSitemapValidation(initialSitemapValidation);
    resetSiteForm();
  };

  const openEditSite = (site: OrganizationSiteResponse) => {
    setSiteForm({
      siteName: site.siteName || '',
      siteUrl: site.siteUrl || '',
      sitemapUrl: site.sitemapUrl || '',
      timezone: site.timezone || '',
      customerName: site.customerName || '',
      customerEmail: site.customerEmail || '',
      isActive: site.isActive ?? true,
    });
    setEditSitemapValidation(initialSitemapValidation);
    setEditingSite(site);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !organization) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/organization')} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">
          Failed to load organization detail: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  const planLabel = normalizePlanTierLabel(organization.planTier);

  return (
    <Box
      sx={{
        maxWidth: { xs: 400, sm: 768, md: 1000, lg: 1200 },
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: 3,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Admin {'>'} Configuration {'>'} Organizations {'>'} {organization.name}
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1.5}
        mb={2.5}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {organization.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage organization, admin/Tenants Users, sites, and properties
          </Typography>
        </Box>

        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/organization')}>
          Back
        </Button>
      </Box>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
              <BusinessIcon color="primary" />
              <Box minWidth={0}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {organization.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {organization.slug || organization.id} • {planLabel} Plan
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={`${totalSites} Sites`} variant="outlined" />
              <Chip size="small" label={`${organization.totalProperties ?? 0} Properties`} variant="outlined" color="secondary" />
              <IconButton size="small" onClick={openEditOrganization} disabled={!operationsEnabled}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => setDeletingOrganization(true)} disabled={!operationsEnabled}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Organization Users</Typography>
            <Button
              size="small"
              variant="text"
              onClick={() => setOrgUsersExpanded((prev) => !prev)}
              endIcon={orgUsersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {orgUsersExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </Box>
          <Collapse in={orgUsersExpanded}>
            <Divider sx={{ my: 1.5 }} />
            {organizationUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No organization users found.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {organizationUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.name || '-'}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role || user.profile || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Collapse>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Agency Users</Typography>
            <Button
              size="small"
              variant="text"
              onClick={() => setAgencyUsersExpanded((prev) => !prev)}
              endIcon={agencyUsersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {agencyUsersExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </Box>
          <Collapse in={agencyUsersExpanded}>
            <Divider sx={{ my: 1.5 }} />
            {agencyUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No agency users found.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agencyUsers.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{user.name || '-'}</TableCell>
                        <TableCell>{user.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Collapse>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ py: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle2">Sites</Typography>
            <Tooltip title={addSiteDisabledReason} disableHoverListener={!addSiteDisabledReason}>
              <span>
                <Button
                  size="small"
                  variant="text"
                  endIcon={<AddIcon />}
                  onClick={openCreateSite}
                  disabled={addSiteDisabled}
                >
                  Add Site
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {sortedSites.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No sites found.</Typography>
          ) : (
            <Stack spacing={1.25}>
              {sortedSites.map((site) => (
                <Card key={site.id} variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
                      <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
                        <LanguageIcon color="action" fontSize="small" />
                        <Box minWidth={0}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>{site.siteName}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                            {site.siteUrl}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                        <Chip size="small" label={site.isActive ? 'Active' : 'Inactive'} color={site.isActive ? 'success' : 'default'} />
                        <Chip size="small" label={(site.mode || 'N/A').replace('_', ' ')} variant="outlined" />
                        <Chip size="small" label={`${site.totalProperties ?? (site.properties?.length || 0)} Properties`} variant="outlined" />
                        <Tooltip title="Edit Site">
                          <IconButton size="small" onClick={() => openEditSite(site)} disabled={!operationsEnabled}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Site">
                          <IconButton size="small" color="error" onClick={() => setDeletingSite(site)} disabled={!operationsEnabled}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => toggleSiteExpanded(site.id)}
                          endIcon={expandedSiteIds.has(site.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                          {expandedSiteIds.has(site.id) ? 'Collapse' : 'Expand'}
                        </Button>
                      </Stack>
                    </Box>

                    <Collapse in={expandedSiteIds.has(site.id)}>
                      <Divider sx={{ my: 1.25 }} />

                      <Card variant="outlined" sx={{ mb: 1.25 }}>
                        <CardContent sx={{ py: 1.25 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25}>
                            <Typography variant="subtitle2">Site Information</Typography>
                            <Button size="small" variant="text" endIcon={<ExpandLessIcon />}>Collapse</Button>
                          </Box>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={3}>
                              <Typography variant="caption" color="text.secondary">Site URL</Typography>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{site.siteUrl}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="caption" color="text.secondary">Timezone</Typography>
                              <Typography variant="body2">{site.timezone || 'UTC'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="caption" color="text.secondary">Active Status</Typography>
                              <Typography variant="body2">{site.isActive ? 'true' : 'false'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                              <Typography variant="body2">{site.customerName || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="caption" color="text.secondary">Customer Email</Typography>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{site.customerEmail || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="caption" color="text.secondary">Site Tier</Typography>
                              <Typography variant="body2">{site.siteTier || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="caption" color="text.secondary">Agency Code</Typography>
                              <Typography variant="body2">{site.agencyCode || '-'}</Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>

                      <Card variant="outlined" sx={{ mb: 1.25 }}>
                        <CardContent sx={{ py: 1.25 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={propertiesExpandedSiteIds.has(site.id) ? 1.25 : 0}>
                            <Typography variant="subtitle2">Properties</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Button size="small" variant="text" endIcon={<AddIcon />} disabled>
                                Add Property
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => togglePropertiesExpanded(site.id)}
                                endIcon={propertiesExpandedSiteIds.has(site.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              >
                                {propertiesExpandedSiteIds.has(site.id) ? 'Collapse' : 'Expand'}
                              </Button>
                            </Stack>
                          </Box>

                          <Collapse in={propertiesExpandedSiteIds.has(site.id)}>
                            <Divider sx={{ mb: 1.25 }} />
                            {site.properties && site.properties.length > 0 ? (
                              <Stack spacing={0.75}>
                                {site.properties.map((property) => (
                                  <Box key={property.id} display="flex" alignItems="center" gap={1}>
                                    <SettingsIcon fontSize="small" color="action" />
                                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                      {property.displayName || property.propertyRef}
                                    </Typography>
                                    <Chip size="small" label={property.isVerified ? 'Verified' : 'Unverified'} color={property.isVerified ? 'success' : 'warning'} />
                                    <Chip size="small" label={property.isActive ? 'Active' : 'Inactive'} color={property.isActive ? 'success' : 'default'} />
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No properties configured</Typography>
                            )}
                          </Collapse>
                        </CardContent>
                      </Card>

                      <SiteNotificationReportsSection
                        site={site}
                        expanded={notificationsExpandedSiteIds.has(site.id)}
                        onToggle={() => toggleNotificationsExpanded(site.id)}
                        operationsEnabled={operationsEnabled}
                      />
                    </Collapse>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={editingOrganization} onClose={() => setEditingOrganization(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          {updateOrganizationMutation.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{updateOrganizationMutation.error.message}</Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            fullWidth
            value={organizationName}
            onChange={(event) => setOrganizationName(event.target.value)}
            disabled={updateOrganizationMutation.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingOrganization(false)} disabled={updateOrganizationMutation.isPending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => updateOrganizationMutation.mutate()}
            disabled={updateOrganizationMutation.isPending || !organizationName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deletingOrganization} onClose={() => setDeletingOrganization(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          {deleteOrganizationMutation.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteOrganizationMutation.error.message}</Alert>
          )}
          <Typography variant="body2">
            Delete {organization.name}? This also deletes its sites and properties.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingOrganization(false)} disabled={deleteOrganizationMutation.isPending}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteOrganizationMutation.mutate()} disabled={deleteOrganizationMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={creatingSite} onClose={closeCreateSite} maxWidth="sm" fullWidth>
        <DialogTitle>Add Site</DialogTitle>
        <DialogContent>
          {createSiteMutation.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{createSiteMutation.error.message}</Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Site Name"
            fullWidth
            value={siteForm.siteName}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, siteName: event.target.value }))}
            disabled={createSiteMutation.isPending}
          />
          <TextField
            margin="dense"
            label="Site URL"
            fullWidth
            value={siteForm.siteUrl}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, siteUrl: event.target.value }))}
            disabled={createSiteMutation.isPending}
          />
          <TextField
            margin="dense"
            label="Sitemap URL"
            fullWidth
            value={siteForm.sitemapUrl}
            onChange={(event) => {
              setSiteForm((prev) => ({ ...prev, sitemapUrl: event.target.value }));
              setCreateSitemapValidation(initialSitemapValidation);
            }}
            helperText="Use a lowercase .xml sitemap URL."
            disabled={createSiteMutation.isPending}
          />
          <SitemapValidationPanel
            siteUrl={siteForm.siteUrl}
            sitemapUrl={siteForm.sitemapUrl}
            validation={createSitemapValidation}
            disabled={createSiteMutation.isPending}
            onValidate={() => validateSitemapSource(siteForm.siteUrl, siteForm.sitemapUrl, setCreateSitemapValidation)}
          />
          <TextField
            margin="dense"
            label="Timezone"
            fullWidth
            value={siteForm.timezone}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, timezone: event.target.value }))}
            disabled={createSiteMutation.isPending}
          />
          <TextField
            margin="dense"
            label="Customer Name"
            fullWidth
            value={siteForm.customerName}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, customerName: event.target.value }))}
            disabled={createSiteMutation.isPending}
          />
          <TextField
            margin="dense"
            label="Customer Email"
            fullWidth
            value={siteForm.customerEmail}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, customerEmail: event.target.value }))}
            disabled={createSiteMutation.isPending}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={(
              <Switch
                checked={siteForm.isActive}
                onChange={(event) => setSiteForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                disabled={createSiteMutation.isPending}
              />
            )}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateSite} disabled={createSiteMutation.isPending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createSiteMutation.mutate()}
            disabled={
              createSiteMutation.isPending
              || !siteForm.siteName.trim()
              || !siteForm.siteUrl.trim()
              || !siteForm.sitemapUrl.trim()
              || !canCreateSiteWithSitemap
              || addSiteDisabled
            }
          >
            Add Site
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editingSite} onClose={() => setEditingSite(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Site</DialogTitle>
        <DialogContent>
          {updateSiteMutation.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{updateSiteMutation.error.message}</Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Site Name"
            fullWidth
            value={siteForm.siteName}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, siteName: event.target.value }))}
            disabled={updateSiteMutation.isPending}
          />
          <TextField
            margin="dense"
            label="Site URL"
            fullWidth
            value={siteForm.siteUrl}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, siteUrl: event.target.value }))}
            disabled={updateSiteMutation.isPending}
          />
          <TextField
            margin="dense"
            label="Sitemap URL"
            fullWidth
            value={siteForm.sitemapUrl}
            onChange={(event) => {
              setSiteForm((prev) => ({ ...prev, sitemapUrl: event.target.value }));
              setEditSitemapValidation(initialSitemapValidation);
            }}
            helperText="Use a lowercase .xml sitemap URL."
            disabled={updateSiteMutation.isPending}
          />
          <SitemapValidationPanel
            siteUrl={siteForm.siteUrl}
            sitemapUrl={siteForm.sitemapUrl}
            validation={editSitemapValidation}
            disabled={updateSiteMutation.isPending || !editingSitemapChanged}
            onValidate={() => validateSitemapSource(siteForm.siteUrl, siteForm.sitemapUrl, setEditSitemapValidation)}
          />
          <TextField
            margin="dense"
            label="Timezone"
            fullWidth
            value={siteForm.timezone}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, timezone: event.target.value }))}
            disabled={updateSiteMutation.isPending}
          />
          <TextField
            margin="dense"
            label="Customer Name"
            fullWidth
            value={siteForm.customerName}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, customerName: event.target.value }))}
            disabled={updateSiteMutation.isPending}
          />
          <TextField
            margin="dense"
            label="Customer Email"
            fullWidth
            value={siteForm.customerEmail}
            onChange={(event) => setSiteForm((prev) => ({ ...prev, customerEmail: event.target.value }))}
            disabled={updateSiteMutation.isPending}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={(
              <Switch
                checked={siteForm.isActive}
                onChange={(event) => setSiteForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                disabled={updateSiteMutation.isPending}
              />
            )}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingSite(null)} disabled={updateSiteMutation.isPending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => updateSiteMutation.mutate()}
            disabled={updateSiteMutation.isPending || !siteForm.siteName.trim() || !siteForm.siteUrl.trim() || !canEditSiteWithSitemap}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deletingSite} onClose={() => setDeletingSite(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Site</DialogTitle>
        <DialogContent>
          {deleteSiteMutation.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteSiteMutation.error.message}</Alert>
          )}
          <Typography variant="body2">
            Delete {deletingSite?.siteName}? This also deletes its properties.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingSite(null)} disabled={deleteSiteMutation.isPending}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => deleteSiteMutation.mutate()} disabled={deleteSiteMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationDetailPage;
